import { Chip } from '@/components/ui'
import type { Stage, StageHistoryEntry } from '../api/types'
import { STAGE_LABELS, STAGE_VARIANT } from '../utils/stage-meta'

/**
 * Vertical timeline of a packet's stage_history. Simpler than
 * sales-inbox's version — this pipeline has no synthetic
 * `extraction:<state>` markers or CARLA anomaly entries, just plain
 * `{stage, at, note?, actor?}` transitions appended by
 * `export_docs_pipeline.record_stage`.
 */
export function StageHistoryTimeline({ entries }: { entries: StageHistoryEntry[] }) {
  if (!entries || entries.length === 0) {
    return <p className="m-0 text-sm text-tertiary">No stage transitions recorded yet.</p>
  }

  // Newest first — the JSONB array is appended chronologically server-side.
  const display = [...entries].reverse()

  return (
    <ol className="flex flex-col gap-2">
      {display.map((entry, i) => {
        const stage = entry.stage as Stage
        const label = STAGE_LABELS[stage] ?? entry.stage
        const variant = STAGE_VARIANT[stage] ?? 'neutral'
        return (
          <li
            key={`${entry.at}-${entry.stage}-${i}`}
            className="flex items-start gap-3 border-l border-border-subtle pl-3"
          >
            <Chip size="xs" variant={variant} dot>
              {label}
            </Chip>
            <div className="min-w-0 flex-1">
              <div className="text-role-metadata">
                {formatTimestamp(entry.at)}
                {entry.actor ? ` · ${entry.actor}` : null}
              </div>
              {entry.note ? <div className="mt-0.5 text-xs text-secondary">{entry.note}</div> : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return iso
  }
}
